import React, { useContext, useRef } from 'react';

import { CountingCirclesRecord, PollSubType } from 'api/api.types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Loading } from '@ktag/styleguide/dist/react';
import ApiContext from 'api/ApiContext';
import useMapApi from '../useMapApi';
import MapTooltip from '../MapTooltip/MapTooltip';
import WorldMap from '../WorldMap';
import MapKey from './PollMapKey';

export const VOTERS_ABROAD_NAME = 'Auslandschweizer';

interface Props {
  year?: string | number;
  records: CountingCirclesRecord[];
  mapOutline: string | null;
  isNational: boolean;
  subtype: PollSubType;
}

const Map = ({ year, records, mapOutline, isNational, subtype }: Props) => {
  const { t } = useTranslation('wap');
  const { language } = useContext(ApiContext);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const { map, hoveredElement, isLoading } = useMapApi({
    year,
    records,
    type: 'poll',
  });

  const { results } = hoveredElement ?? {};

  const resultI18nKey = subtype === 'tie_break' ? 'vi' : 'yes';

  const hasYesVotes =
    results?.poll_data?.yes_votes_percentage ||
    results?.poll_data?.yes_votes_percentage === 0;

  const tooltipResultString =
    hasYesVotes &&
    `${results?.poll_data?.yes_votes_percentage.toFixed(2)} % ${t(resultI18nKey)}`;

  const isResultYes = results?.poll_data?.yes_votes > results?.poll_data?.no_votes;

  const tooltipResult = (
    <div
      className={classNames('tooltip__result ', {
        'tooltip__result--yes': isResultYes,
      })}
    >
      {tooltipResultString}
    </div>
  );

  return (
    <div
      className={classNames('pollmap', {
        [`pollmap--${mapOutline}`]: mapOutline,
      })}
      ref={wrapperRef}
    >
      {isLoading && <Loading size="big" />}
      {!isLoading && !map && <p>{t('map_not_available')}</p>}
      {!isLoading && map && (
        <>
          <p className="pollmap__screenreaderinfo">{t('map_screen_reader_info')}</p>
          <div className="pollmap__container" aria-hidden>
            <div
              className="pollmap__map"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: map }}
            />
            <WorldMap isNational={isNational} records={records} map={map} />
          </div>
          <MapTooltip
            title={hoveredElement?.name?.[language]}
            bfsNumber={hoveredElement?.bfs_number}
            wrapperRef={wrapperRef}
            result={tooltipResult}
          />
          <MapKey
            yesCount={hoveredElement?.results?.poll_data.yes_votes_percentage}
            subtype={subtype}
          />
        </>
      )}
    </div>
  );
};

export default Map;
